import React, { useState, useEffect } from 'react';
import { settingsAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../lib/utils';
import { Plus, Trash2, Edit2, Check, X, Tag, Layers, CircleDot, Users, Loader2, Shield, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const TABS = [
  { key: 'statuses', label: 'Statuses', icon: CircleDot },
  { key: 'labels', label: 'Labels', icon: Tag },
  { key: 'modules', label: 'Modules', icon: Layers },
  { key: 'users', label: 'Users', icon: Users },
];

const COLORS = ['#6b7280','#3b82f6','#f59e0b','#10b981','#8b5cf6','#ef4444','#ec4899','#06b6d4','#22c55e','#f97316'];

export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('statuses');
  const [statuses, setStatuses] = useState([]);
  const [labels, setLabels] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState('');
  const [newColor, setNewColor] = useState('#6b7280');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  useEffect(() => { fetchData(); }, [tab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'statuses') {
        const res = await settingsAPI.getStatuses();
        setStatuses(res.data);
      } else if (tab === 'labels') {
        const res = await settingsAPI.getLabels();
        setLabels(res.data);
      } else if (tab === 'modules') {
        const res = await settingsAPI.getModules();
        setModules(res.data);
      } else if (tab === 'users') {
        const res = await userAPI.getUsers();
        setUsers(res.data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    try {
      if (tab === 'statuses') {
        await settingsAPI.createStatus({ name: newItem, color: newColor });
      } else if (tab === 'labels') {
        await settingsAPI.createLabel({ name: newItem, color: newColor });
      } else if (tab === 'modules') {
        await settingsAPI.createModule({ name: newItem });
      }
      setNewItem('');
      setNewColor('#6b7280');
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      if (tab === 'labels') await settingsAPI.deleteLabel(id);
      else if (tab === 'modules') await settingsAPI.deleteModule(id);
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setEditName(item.name);
    setEditColor(item.color || '#6b7280');
  };

  const handleSaveEdit = async () => {
    try {
      if (tab === 'statuses') {
        await settingsAPI.updateStatus(editId, { name: editName, color: editColor });
      } else if (tab === 'labels') {
        await settingsAPI.updateLabel(editId, { name: editName, color: editColor });
      } else if (tab === 'modules') {
        await settingsAPI.updateModule(editId, { name: editName });
      }
      setEditId(null);
      fetchData();
    } catch (e) { console.error(e); }
  };

  const renderList = (items, showColor = false, canDelete = true) => (
    <div className="space-y-1.5">
      {items.map((item) => (
        <div key={item.id} className="flex items-center justify-between px-4 py-2.5 bg-white border border-slate-200 rounded-lg group hover:border-slate-300 transition-colors" data-testid={`settings-item-${item.id}`}>
          {editId === item.id ? (
            <div className="flex items-center gap-2 flex-1">
              {showColor && (
                <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} className="w-7 h-7 border-0 rounded cursor-pointer" />
              )}
              <input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 text-sm border border-slate-200 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-[#002FA7]" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()} />
              <button onClick={handleSaveEdit} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="h-4 w-4" /></button>
              <button onClick={() => setEditId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2.5">
                {showColor && <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: item.color || '#6b7280' }} />}
                <span className="text-sm text-slate-700 font-medium">{item.name}</span>
                {item.key && <span className="text-[10px] text-slate-400 font-mono">({item.key})</span>}
              </div>
              <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleEdit(item)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" data-testid={`edit-btn-${item.id}`}><Edit2 className="h-3.5 w-3.5" /></button>
                {canDelete && (
                  <button onClick={() => handleDelete(item.id)} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`delete-btn-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </>
          )}
        </div>
      ))}
      {items.length === 0 && !loading && (
        <p className="text-sm text-slate-400 text-center py-8">No items yet. Add your first one above.</p>
      )}
    </div>
  );

  const [editUserId, setEditUserId] = useState(null);
  const [editUserRole, setEditUserRole] = useState('');
  const [editUserDept, setEditUserDept] = useState('');
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);

  const handleUpdateUserRole = async (userId) => {
    try {
      const u = users.find(x => x.id === userId);
      if (!u) return;
      await userAPI.updateUser(userId, {
        name: u.name, email: u.email || '', password: '',
        organization: u.organization || 'Intelvolks',
        department: editUserDept || u.department || '',
        role: editUserRole || u.role,
      });
      setEditUserId(null);
      fetchData();
    } catch (e) { console.error('Update user error:', e); }
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirmUser) return;
    try {
      await userAPI.deleteUser(deleteConfirmUser.id);
      setDeleteConfirmUser(null);
      fetchData();
    } catch (e) { console.error('Delete user error:', e); }
  };

  const renderUsersTab = () => (
    <div className="space-y-1.5">
      {users.map(u => (
        <div key={u.id} className="bg-white border border-slate-200 rounded-lg group hover:border-slate-300 transition-colors px-4 py-2.5" data-testid={`user-item-${u.id}`}>
          {editUserId === u.id ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-[#002FA7] flex-shrink-0">
                  {getInitials(u.name)}
                </div>
                <p className="text-sm font-medium text-slate-700">{u.name}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select value={editUserRole} onChange={(e) => setEditUserRole(e.target.value)}
                  className="h-7 px-2 text-xs border border-slate-200 rounded bg-white outline-none">
                  <option value="user">User</option>
                  <option value="support">Support</option>
                  <option value="developer">Developer</option>
                  <option value="pm">PM</option>
                  <option value="admin">Admin</option>
                </select>
                <input value={editUserDept} onChange={(e) => setEditUserDept(e.target.value)}
                  placeholder="Department" className="h-7 w-28 sm:w-32 px-2 text-xs border border-slate-200 rounded outline-none" />
                <button onClick={() => handleUpdateUserRole(u.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditUserId(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X className="h-4 w-4" /></button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-[#002FA7] flex-shrink-0">
                  {getInitials(u.name)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{u.name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                  u.role === 'admin' ? 'bg-red-50 text-red-600' :
                  u.role === 'pm' ? 'bg-violet-50 text-violet-600' :
                  u.role === 'developer' ? 'bg-blue-50 text-blue-600' :
                  'bg-slate-100 text-slate-500'
                }`}>{u.role}</span>
                <span className="text-[10px] text-slate-400 hidden sm:inline">{u.department || '-'}</span>
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active !== false ? 'bg-green-500' : 'bg-slate-300'}`} title={u.is_active !== false ? 'Active' : 'Inactive'} />
                <div className="flex items-center gap-0.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditUserId(u.id); setEditUserRole(u.role); setEditUserDept(u.department || ''); }}
                    className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded" data-testid={`settings-edit-user-${u.id}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  {u.id !== user?.id && !u.is_protected && (
                    <button onClick={() => setDeleteConfirmUser(u)}
                      className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`settings-delete-user-${u.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const showAddForm = tab !== 'users';
  const showColor = tab === 'statuses' || tab === 'labels';
  const canDelete = tab !== 'statuses';

  return (
    <div data-testid="settings-page">
      <h1 className="text-xl font-heading font-bold text-slate-800 mb-5">Settings</h1>

      {/* Tabs */}
      <div className="overflow-x-auto -mx-1 px-1 mb-6" data-testid="settings-tabs">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit min-w-0">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setEditId(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                tab === t.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`settings-tab-${t.key}`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {showColor && (
              <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-9 h-9 border border-slate-200 rounded-lg cursor-pointer p-0.5 flex-shrink-0" data-testid="settings-add-color" />
            )}
            <input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder={`Add new ${tab === 'labels' ? 'label' : tab === 'modules' ? 'module' : 'status'}...`}
              className="flex-1 min-w-0 h-9 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-1 focus:ring-[#002FA7]"
              data-testid="settings-add-input"
            />
          </div>
          <button
            onClick={handleAdd}
            className="h-9 px-3 bg-[#002FA7] text-white text-sm font-medium rounded-lg hover:bg-[#0035c0] flex items-center justify-center gap-1.5 flex-shrink-0"
            data-testid="settings-add-btn"
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : tab === 'users' ? (
        renderUsersTab()
      ) : tab === 'statuses' ? (
        renderList(statuses, true, false)
      ) : tab === 'labels' ? (
        renderList(labels, true, true)
      ) : tab === 'modules' ? (
        renderList(modules, false, true)
      ) : null}

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deleteConfirmUser} onOpenChange={(open) => !open && setDeleteConfirmUser(null)}>
        <DialogContent className="max-sm:top-[50%] max-sm:left-[50%] max-sm:translate-x-[-50%] max-sm:translate-y-[-50%] max-sm:w-[calc(100%-2rem)] max-sm:h-auto max-sm:max-w-sm max-sm:rounded-xl sm:max-w-sm" data-testid="delete-user-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-600">
              Are you sure you want to delete <span className="font-semibold text-slate-800">{deleteConfirmUser?.name}</span>?
            </p>
            <p className="text-xs text-slate-400 mt-1">This action cannot be undone.</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={() => setDeleteConfirmUser(null)}
              className="h-9 px-4 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid="cancel-delete-user-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              className="h-9 px-4 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1.5"
              data-testid="confirm-delete-user-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
